import React, { useEffect, useState } from 'react';
import { COLOR_SCHEMES, getColorSchemesForExam, getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';
import TmuaPearsonShell, {
  NextIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
} from './TmuaPearsonShell';
import TmuaPearsonDialog from './TmuaPearsonDialog';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';

/**
 * UCAT-only welcome screen shown when a mock or past paper is started
 * (not topic practice). Layout matches the official Practice Test intro
 * (title bar, colour-scheme bar, logo, instructional copy, Next footer)
 * but uses the app's IVORI / COLOR_SCHEMES palette rather than Pearson blue.
 */

const UcatLogo = () => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'stretch',
      flexShrink: 0,
      fontFamily: 'var(--iv-font-body)',
      userSelect: 'none',
    }}
    aria-label="UCAT University Clinical Aptitude Test"
  >
    <div
      style={{
        background: '#7A1F2B',
        color: '#FFFFFF',
        fontWeight: 800,
        fontSize: '22px',
        letterSpacing: '0.04em',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      UCAT
    </div>
    <div
      style={{
        background: '#1A2332',
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: '11px',
        letterSpacing: '0.06em',
        lineHeight: 1.25,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
        maxWidth: '160px',
      }}
    >
      UNIVERSITY CLINICAL APTITUDE TEST
    </div>
  </div>
);

/** Pearson-chrome branch: TmuaPearsonShell wrapper, Next-only footer, no logo, rewritten copy. */
const UcatPearsonWelcomeScreen = ({
  paperTitle = 'Practice Test',
  colorScheme = 'pearsonDefault',
  setColorScheme,
  onNext,
  onEndExam,
}) => {
  const T = TMUA_PEARSON_TOKENS;
  const [showReadyConfirm, setShowReadyConfirm] = useState(false);

  const requestNext = () => setShowReadyConfirm(true);
  const dismissConfirm = () => setShowReadyConfirm(false);
  const confirmNext = () => {
    setShowReadyConfirm(false);
    onNext?.();
  };

  useTmuaPearsonAccessKeys(
    {
      showResponseRequiredVisible: false,
      nextAvailable: !showReadyConfirm,
      endExamAvailable: false,
    },
    {
      onNext: () => { if (!showReadyConfirm) requestNext(); },
      onConfirmYes: () => { if (showReadyConfirm) confirmNext(); },
      onConfirmNo: () => { if (showReadyConfirm) dismissConfirm(); },
    }
  );

  return (
    <TmuaPearsonShell
      examName="UCAT"
      title={paperTitle}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      colorSchemeLabel={getPearsonColorSchemeDropdownLabel('UCAT')}
      onBackToSelection={onEndExam}
      footerLeft={null}
      footerRight={(
        <TmuaPearsonFooterButton variant="chrome" onClick={requestNext} icon={<NextIcon color="#FFFFFF" />} iconPosition="end" underline="n">
          Next
        </TmuaPearsonFooterButton>
      )}
    >
      <div className="tmua-pearson-instruction-copy" style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '18px 22px', color: '#000', fontFamily: T.fontUi, fontSize: 16, lineHeight: 1.35 }}>
        <div style={{ maxWidth: '920px' }}>
          <p style={{ margin: '0 0 10px' }}>Welcome to the UCAT Practice Test.</p>
          <p style={{ margin: '0 0 10px' }}>
            This practice sitting is designed to help you familiarise yourself with the test format, question style, and
            the navigation features of the test system.
          </p>
          <p style={{ margin: '0 0 10px' }}>
            Apart from this screen, the screens that follow are specific to the live exam and are provided for your
            information.
          </p>
          <p style={{ margin: '0 0 10px', fontWeight: 700 }}>Please note:</p>
          <ul style={{ margin: '0 0 10px', paddingLeft: 22 }}>
            <li style={{ marginBottom: 6 }}>This practice sitting does not save your result or provide a scale score.</li>
            <li style={{ marginBottom: 6 }}>
              The final review screen (which will not appear in the live exam) indicates which of your answers are
              correct or incorrect.
            </li>
          </ul>
          <p style={{ margin: '0 0 10px', fontWeight: 700 }}>Calculator</p>
          <p style={{ margin: '0 0 10px' }}>
            An onscreen calculator is available during the test. Click the icon at the top left of the screen to open it.
            It opens in its own window; clicking back onto the test screen (or using a keyboard shortcut such as
            Alt + F to Flag for Review) disables it until you click back on it.
          </p>
          <p style={{ margin: '0 0 10px', fontWeight: 700 }}>Navigation</p>
          <p style={{ margin: '0 0 10px' }}>
            Use the mouse to navigate the test and answer questions. Where a letter is underlined, Alt plus that letter
            is also a shortcut (for example Alt + N for Next). If shortcuts do not work on your device, use the mouse.
          </p>
          <p style={{ margin: '0 0 10px' }}>
            The &apos;Navigator&apos; function at the bottom right of the screen lets you jump to any question within the
            current subtest.
          </p>
          <p style={{ margin: 0 }}>
            Please click the{' '}
            <strong>
              Next (<span style={{ textDecoration: 'underline' }}>N</span>)
            </strong>{' '}
            button when you are ready to begin the practice test.
          </p>
        </div>
      </div>
      {showReadyConfirm ? (
        <TmuaPearsonDialog title="Begin Exam" info onYes={confirmNext} onNo={dismissConfirm}>
          If you are ready to begin the exam, select Yes. Otherwise select No to return to the previous screen.
        </TmuaPearsonDialog>
      ) : null}
    </TmuaPearsonShell>
  );
};

const UcatCreamWelcomeScreen = ({
  paperTitle = 'Practice Test',
  colorScheme = 'ivori',
  setColorScheme,
  onNext,
}) => {
  const schemeOptions = getColorSchemesForExam('UCAT');
  const scheme = schemeOptions[colorScheme] || COLOR_SCHEMES.ivori;
  const ink = scheme.text || 'var(--iv-head)';
  const divider = colorScheme !== 'ivori' ? scheme.border : 'var(--iv-line)';
  const [showReadyConfirm, setShowReadyConfirm] = useState(false);

  const bodyText = {
    fontSize: '16.5px',
    lineHeight: 1.65,
    color: ink,
    margin: '0 0 16px',
    fontFamily: 'var(--iv-font-body)',
  };

  const sectionHeading = {
    fontSize: '16.5px',
    lineHeight: 1.65,
    color: ink,
    margin: '0 0 8px',
    fontWeight: 700,
    fontFamily: 'var(--iv-font-body)',
  };

  const bulletList = {
    margin: '0 0 16px',
    paddingLeft: '22px',
    fontSize: '16.5px',
    lineHeight: 1.65,
    color: ink,
    fontFamily: 'var(--iv-font-body)',
    listStyleType: 'disc',
    listStylePosition: 'outside',
  };

  const bulletItem = {
    marginBottom: '6px',
    display: 'list-item',
    listStyleType: 'disc',
    color: ink,
  };

  const requestNext = () => setShowReadyConfirm(true);
  const dismissConfirm = () => setShowReadyConfirm(false);
  const confirmNext = () => {
    setShowReadyConfirm(false);
    onNext?.();
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.repeat || event.isComposing) return;
      const tag = String(event.target?.tagName || '').toLowerCase();
      if (tag === 'select' || tag === 'input' || tag === 'textarea') return;
      if (event.ctrlKey || event.metaKey) return;

      if (showReadyConfirm) {
        const isY = event.key === 'y' || event.key === 'Y';
        const isN = event.key === 'n' || event.key === 'N';
        const isEsc = event.key === 'Escape';
        if (isY) {
          event.preventDefault();
          confirmNext();
        } else if (isN || isEsc) {
          event.preventDefault();
          dismissConfirm();
        }
        return;
      }

      const isN = event.key === 'n' || event.key === 'N';
      if (!isN) return;
      event.preventDefault();
      requestNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onNext, showReadyConfirm]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: scheme.body,
        color: ink,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--iv-font-body)',
      }}
    >
      {/* Title bar */}
      <div
        style={{
          padding: '12px 28px',
          background: scheme.chrome,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '18px', fontWeight: 700, color: ink }}>
          {paperTitle}
        </span>
      </div>

      {/* Colour scheme bar */}
      <div
        style={{
          padding: '8px 28px',
          background: scheme.chrome,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <select
          value={schemeOptions[colorScheme] ? colorScheme : 'ivori'}
          onChange={(e) => setColorScheme?.(e.target.value)}
          aria-label="Colour Scheme"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: ink,
            fontFamily: 'var(--iv-font-body)',
            background: scheme.chrome,
            border: `1.5px solid ${scheme.border}`,
            borderRadius: '4px',
            padding: '5px 10px',
            cursor: 'pointer',
            minWidth: '160px',
          }}
        >
          {Object.entries(schemeOptions).map(([key, s]) => (
            <option key={key} value={key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '28px 40px 40px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <UcatLogo />
        </div>

        <div style={{ maxWidth: '920px' }}>
          <p style={bodyText}>Welcome to the UCAT Practice Test.</p>
          <p style={bodyText}>
            The exam content and introduction screens of these exams are designed to replicate as much as possible the official UCAT exam.
          </p>
          <p style={bodyText}>
            Apart from this screen, the others you will see are specific to the live exam. They are provided for your information.
          </p>

          <p style={sectionHeading}>Please note:</p>
          <ul style={bulletList}>
            <li style={bulletItem}>
              The practice tests will not save your result or provide a scale score.
            </li>
            <li style={bulletItem}>
              The final review screen (which will not appear in the live exam) indicates which of your answers are correct / incorrect.
            </li>
          </ul>

          <p style={sectionHeading}>Note Taking</p>
          <ul style={bulletList}>
            <li style={bulletItem}>
              If you are sitting your test at a test centre, you will be given a laminated notebook (A4 in size) and a pen.
            </li>
          </ul>

          <p style={sectionHeading}>Calculator</p>
          <ul style={bulletList}>
            <li style={bulletItem}>
              An onscreen calculator is available to assist you in the test. You can access this by clicking on the icon at the top left of the screen.
            </li>
            <li style={bulletItem}>
              You can operate the calculator by using the mouse or the number pad on the keyboard; ensure that &apos;Num Lock&apos; is on for the number pad to work.
            </li>
            <li style={bulletItem}>
              The calculator opens in a separate window to the test screen. If you click off the calculator window back to the test screen (or use a keyboard shortcut, for example Alt + F to Flag for Review) the calculator will not function until you click back on it.
            </li>
          </ul>

          <p style={sectionHeading}>Navigation</p>
          <ul style={bulletList}>
            <li style={bulletItem}>
              You can use the mouse to navigate the test and answer questions.
            </li>
            <li style={bulletItem}>
              If there is a letter underlined, a keyboard shortcut is also available. The Alt key can be used with the underlined letter to carry out a function. For example, Alt + N = Next.
            </li>
            <li style={bulletItem}>
              If the standard shortcuts do not work for you in the practice materials this may be because of the device, operating system or browser you are using. You are advised to use the mouse to navigate through the test.
            </li>
          </ul>

          <p style={bodyText}>
            The &apos;Navigator&apos; function at the bottom right of the screen allows you to navigate to questions within the subtest.
          </p>

          <p style={{ ...bodyText, marginBottom: 0 }}>
            Please click the{' '}
            <strong>
              Next (<span style={{ textDecoration: 'underline' }}>N</span>)
            </strong>{' '}
            button when you are ready to begin the practice test.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '14px 28px',
          borderTop: `1px solid ${divider}`,
          background: scheme.chrome,
        }}
      >
        <button
          type="button"
          onClick={requestNext}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600,
            color: ink,
            background: scheme.chrome,
            border: `1.5px solid ${scheme.border}`,
            borderRadius: '4px',
            padding: '8px 20px',
            cursor: 'pointer',
            fontFamily: 'var(--iv-font-body)',
          }}
        >
          <span>
            <span style={{ textDecoration: 'underline' }}>N</span>ext
          </span>
          <span aria-hidden="true">→</span>
        </button>
      </div>

      {showReadyConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'color-mix(in srgb, var(--iv-band) 65%, transparent)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ucat-ready-confirm-title"
        >
          <div
            style={{
              width: '480px',
              maxWidth: '92vw',
              background: scheme.chrome,
              border: `1px solid ${scheme.border}`,
              borderRadius: '16px',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
              padding: '32px 28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              fontFamily: 'var(--iv-font-body)',
              color: ink,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  background: colorScheme === 'ivori' ? 'color-mix(in srgb, var(--iv-danger) 10%, transparent)' : scheme.body,
                  border: `1.5px solid ${scheme.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colorScheme === 'ivori' ? 'var(--iv-danger)' : '#D92D20',
                  fontSize: 20,
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}
              >
                !
              </div>
              <h3
                id="ucat-ready-confirm-title"
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: 700,
                  color: ink,
                  fontFamily: 'var(--iv-font-head)',
                }}
              >
                Begin Exam
              </h3>
            </div>

            <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.6, color: ink }}>
              If you are ready to begin the exam, select the Yes button. Otherwise, select the No button to return to the previous screen.
            </p>

            <div style={{ display: 'flex', gap: '12px', marginTop: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={dismissConfirm}
                style={{
                  padding: '10px 22px',
                  borderRadius: '999px',
                  border: `1px solid ${scheme.border}`,
                  background: scheme.chrome,
                  color: ink,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                No
              </button>
              <button
                type="button"
                onClick={confirmNext}
                style={{
                  padding: '10px 22px',
                  borderRadius: '999px',
                  border: 'none',
                  background: ink,
                  color: scheme.body,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Dispatcher: Pearson chrome iff pearsonSitting; else today's cream chrome (unchanged for orphans/fail-closed). */
const UcatWelcomeScreen = ({ pearsonSitting = false, ...rest }) => (
  pearsonSitting
    ? <UcatPearsonWelcomeScreen {...rest} />
    : <UcatCreamWelcomeScreen {...rest} />
);

export default UcatWelcomeScreen;
